import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, ProtectedRoute, LoginPage } from './features/auth';
import { AdminLayout } from './components/layout';
import { UsersPage, PartnersPage } from './features/users';
import { SSWContentPage, SSWContentEditor, BlogPage } from './features/content';
import { NamesPage, NameSuggestionsPage, NameStatsPage } from './features/names';
import { ChecklistsPage } from './features/checklists';
import { AnalyticsPage, FeatureAnalyticsPage } from './features/analytics';
import { FeedbackPage, BetaFeedbackPage, GDPRPage, ContactRequestsPage } from './features/support';
import { FAQCategoriesPage, FAQItemsPage } from './features/faq';
import { FeatureTogglesPage, LimitsPage, SystemSettingsPage, BetaCodesPage, LegalSettingsPage, PremiumContentPage, DashboardSettingsPage, ToolMenuPage } from './features/settings';
import { MidwivesPage } from './features/midwives';
import { CalendarAdminPage } from './features/calendar';
import { AIChatAdminPage } from './features/ai-chat';
import { ContractionTimerAdminPage } from './features/contraction-timer';
import { BabyCareAdminPage } from './features/baby-care';
import { SleepSoundsAdminPage } from './features/sleep-sounds';
import {
  BabyDevelopmentPhasesPage,
  BabyDevelopmentPhaseEditPage,
  BabyDevelopmentMilestonesPage,
  BabyDevelopmentContentPage,
  BabyDevelopmentActivitiesPage,
  BabyDevelopmentMedicalPage,
} from './features/baby-development';
import { ProductCheckAdminPage, ProductSuggestionsPage, ManualProductsPage } from './features/product-check';
import { BroadcastPage, TemplatesPage } from './features/notifications';
import { GuidesPage, GuideCategoriesPage, GuideTermsPage, GuideFAQsPage, GuideSubmissionsPage } from './features/guides';
import { RecommendationsPage } from './features/recommendations';

// Pages
import DashboardPage from './pages/DashboardPage';

// Placeholder pages - will be replaced with actual implementations
function PlaceholderPage({ title, description }: { title: string; description?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">{title}</h1>
      {description && <p className="text-slate-600">{description}</p>}
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Route - Login */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected Routes - Admin Panel */}
            <Route
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard */}
              <Route path="/" element={<DashboardPage />} />

              {/* Users */}
              <Route path="/users" element={<UsersPage />} />
              <Route path="/partners" element={<PartnersPage />} />

              {/* Content */}
              <Route path="/content/ssw" element={<SSWContentPage />} />
              <Route path="/content/ssw/:week/edit" element={<SSWContentEditor />} />
              <Route path="/content/blog" element={<BlogPage />} />
              <Route path="/content/notifications/broadcast" element={<BroadcastPage />} />
              <Route path="/content/notifications/templates" element={<TemplatesPage />} />
              <Route path="/content/recommendations" element={<RecommendationsPage />} />

              {/* Names */}
              <Route path="/names" element={<NamesPage />} />
              <Route path="/names/suggestions" element={<NameSuggestionsPage />} />
              <Route path="/names/stats" element={<NameStatsPage />} />

              {/* Checklists */}
              <Route path="/checklists" element={<ChecklistsPage />} />

              {/* Features */}
              <Route path="/features/calendar" element={<CalendarAdminPage />} />
              <Route path="/features/ai-chat" element={<AIChatAdminPage />} />
              <Route path="/features/contraction-timer" element={<ContractionTimerAdminPage />} />
              <Route path="/features/baby-care" element={<BabyCareAdminPage />} />
              <Route path="/features/sleep-sounds" element={<SleepSoundsAdminPage />} />
              <Route path="/features/product-check" element={<ProductCheckAdminPage />} />
              <Route path="/features/product-check/suggestions" element={<ProductSuggestionsPage />} />
              <Route path="/features/product-check/products" element={<ManualProductsPage />} />

              {/* Baby Development */}
              <Route path="/features/baby-development" element={<BabyDevelopmentPhasesPage />} />
              <Route path="/features/baby-development/phases/:id/edit" element={<BabyDevelopmentPhaseEditPage />} />
              <Route path="/features/baby-development/phases/:id/milestones" element={<BabyDevelopmentMilestonesPage />} />
              <Route path="/features/baby-development/phases/:id/content" element={<BabyDevelopmentContentPage />} />
              <Route path="/features/baby-development/phases/:id/activities" element={<BabyDevelopmentActivitiesPage />} />
              <Route path="/features/baby-development/phases/:id/medical" element={<BabyDevelopmentMedicalPage />} />

              {/* Behördenguides */}
              <Route path="/features/guides" element={<GuidesPage />} />
              <Route path="/features/guides/categories" element={<GuideCategoriesPage />} />
              <Route path="/features/guides/terms" element={<GuideTermsPage />} />
              <Route path="/features/guides/faqs" element={<GuideFAQsPage />} />
              <Route path="/features/guides/submissions" element={<GuideSubmissionsPage />} />

              {/* Analytics */}
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/analytics/features" element={<FeatureAnalyticsPage />} />

              {/* FAQ */}
              <Route path="/faq/categories" element={<FAQCategoriesPage />} />
              <Route path="/faq/items" element={<FAQItemsPage />} />

              {/* Support */}
              <Route path="/support/requests" element={<ContactRequestsPage />} />
              <Route path="/support/beta-feedback" element={<BetaFeedbackPage />} />
              <Route path="/support/feedback" element={<FeedbackPage />} />
              <Route path="/support/gdpr" element={<GDPRPage />} />

              {/* Features - Management */}
              <Route path="/features/toggles" element={<FeatureTogglesPage />} />
              <Route path="/features/limits" element={<LimitsPage />} />
              <Route path="/features/dashboard" element={<DashboardSettingsPage />} />
              <Route path="/features/tool-menu" element={<ToolMenuPage />} />

              {/* Settings */}
              <Route path="/settings/beta-codes" element={<BetaCodesPage />} />
              <Route path="/settings/premium-content" element={<PremiumContentPage />} />
              <Route path="/settings/legal" element={<LegalSettingsPage />} />
              <Route path="/settings/system" element={<SystemSettingsPage />} />

              {/* Legacy redirects for old settings paths */}
              <Route path="/settings/features" element={<Navigate to="/features/toggles" replace />} />
              <Route path="/settings/limits" element={<Navigate to="/features/limits" replace />} />

              {/* Midwives (Coming Soon) */}
              <Route path="/midwives" element={<MidwivesPage />} />

              {/* Legacy redirects */}
              <Route path="/tools/scanner" element={<Navigate to="/features/product-check" replace />} />
            </Route>

            {/* Catch-all - redirect to dashboard */}
            <Route path="*" element={<PlaceholderPage title="404" description="Seite nicht gefunden" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
